<script>
  import LibEventBus from 'utils/libEventBus';
  import nextFrame from 'utils/nextFrame';
  import _ from 'lodash';
  _.noConflict();

  export default {
    name: 'MTableWidthMixin',
    props: {
      fillContainer: Boolean, // content width should be >= 100%
      fitContainer: Boolean // content width should be === 100%
    },
    data () {
      return {
        columnSizeLimits: {} // map of columns minSizes
      };
    },
    computed: {

      /**
       * Column is static if it has `static=true` or `resizable=false` prop
       * @returns {Set<String>} - set of static column keys
       */
      staticColumnKeys () {
        return new Set(
          this.orderedIndexes.map(columnIndex => this.columnsMap[columnIndex]).
            filter(item => item.static || !(item.resizable || true)).
            map(i => i.itemKey)
        );
      },

      /**
       * Get width of columns
       * @returns {Array} of column width in pixels or auto [50, 25, 'auto']
       */
      columnsDimensions () {
        return this.orderedIndexes.map(i => {
          if (this.tableParams[i]) {
            return this.tableParams[i].width;
          }
          return 'auto';
        });
      },

      /**
       * get formatted css style for `grid-template-columns`
       * converts numbers to percents (concatenates number with `%`) and returns non numeric values as it is
       * @returns {string} css param
       */
      gridTemplateColumns () {
        return this.columnsDimensions.map(i => {
          if (typeof i === 'number') {
            return `${i}px`;
          }
          return i;
        }).
          join(' ');
      }
    },
    watch: {
      orderedIndexes () {
        // watch for columns change to update table layout
        this.forceUpdateColumnsDimensions();
      },
      fillContainer () {
        this.forceUpdateColumnsDimensions();
      },
      fitContainer () {
        this.forceUpdateColumnsDimensions();
      }
    },
    created () {
      this.class['m-table_fit-container'] = this.fitContainer;
      this.registerAfterHeaderRenderedHook(this.widthAfterRenderHeaderHook.bind(this));

      LibEventBus.$on('PAGE_RESIZE', this.afterResizeUpdate);
      this.$on('hook:destroyed', () => {
        LibEventBus.$off('PAGE_RESIZE', this.afterResizeUpdate);
      });
    },
    mounted () {
      this.forceUpdateColumnsDimensions();
    },
    methods: {
      fixTableParamsAutoValues () {
        this.orderedIndexes.filter(
          itemKey => typeof this.tableParams[itemKey] !== 'object' || typeof this.tableParams[itemKey].width !== 'number'
        ).
          forEach(itemKey => {
            const header = this.getHeader(itemKey);
            if (!header){
              // no header returned if table is destroyed (happens because this function is called in nextFrame)
              return;
            }
            this.setColumnWidth(itemKey, header.getWidth());
          });
      },
      widthAfterRenderHeaderHook (renderedHeader) {
        const props = renderedHeader.componentOptions.propsData;
        this.columnSizeLimits[props.itemKey] = props.minWidth;
      },

      /**
       * Method to recalculate table dimensions (min width of table is 100%)
       */
      forceUpdateColumnsDimensions () {
        nextFrame(() => {
          this.fixTableParamsAutoValues();
          this.afterResizeUpdate();
        });
      },

      afterResizeUpdate () {
        if (!_.isEmpty(this.tableParams)) {
          this.updateFitSizes();
          this.$emit('update-params', this.tableParams);
        }
      },

      /**
       * Set table width in tableParams
       * @param name - name (index) of column
       * @param width - width in px
       */
      setColumnWidth (name, width) {
        if (!this.tableParams[name]) {
          this.$set(this.tableParams, name, {});
        }
        this.$set(this.tableParams[name], 'width', width);
      },

      getTableExtraSize () {
        const sumSize = this.orderedIndexes.reduce((acc, index) => (this.tableParams[index]?.width || 0) + acc, 0);
        const containerWidth = this.$el.clientWidth;
        return containerWidth - sumSize;
      },

      subtractDiffFromColumns (columnsList, activeColumnKey = null) {
        if (columnsList.length === 0) {
          return;
        }

        const diff = this.getTableExtraSize();
        const delta = Math.floor(diff / columnsList.length);

        const overSubtractedColumns = [];

        columnsList.forEach(i => {
          let newSize = this.tableParams[i].width + delta;

          // size is smaller than min allowed size of column
          if (newSize <= this.columnSizeLimits[i]) {
            overSubtractedColumns.push(i);
            newSize = this.columnSizeLimits[i];
          }

          this.setColumnWidth(i, newSize);
        });

        let remainDiff = this.getTableExtraSize();

        if (remainDiff) {
          const columnsToResize = columnsList.filter(i => !overSubtractedColumns.includes(i));

          /*
           * if remain diff cannot be divided by columns count without remain
           * subtract/add 1px while remain is not compensated
           */
          if (Math.abs(remainDiff) < columnsToResize.length) {
            const sign = remainDiff > 0 ? 1 : -1;
            columnsToResize.forEach(i => {
              if (remainDiff === 0) {
                return;
              }
              this.tableParams[i].width += sign;
              remainDiff -= sign;
            });
          } else {

            // of remain can be divided by columns count call recursion
            this.subtractDiffFromColumns(columnsToResize, activeColumnKey);
          }

          // if all columns are already baby-sized than subtract/add difference to column which is resizing
          if (activeColumnKey) {
            this.tableParams[activeColumnKey].width += this.getTableExtraSize();
          }
        }
      },

      /**
       * Update columns sizes to fill table container (sum of columns sizes === 100% of container)
       * @param activeColumnKey String - key of column that is currently resizing,
       * may be null if function is called during table initialisation
       */
      updateFitSizes (activeColumnKey = null) {
        if (!this.fitContainer && !this.fillContainer) {
          return;
        }

        const fixedColumns = this.staticColumnKeys;
        const diff = this.getTableExtraSize();

        // diff !== 0 if table should be in container, diff > 0 if table should be bigger than container
        if (diff !== 0 && this.fitContainer || diff > 0 && this.fillContainer) {
          this.subtractDiffFromColumns(
            this.orderedIndexes.filter(i => !fixedColumns.has(i) && i !== activeColumnKey),
            activeColumnKey
          );
        }
      }
    }
  };
</script>
